import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Input, Button, Icon, FilterChip } from '@getpay93/ui';
import { useAuth } from '../auth/AuthContext';
import { homePathFor } from '../auth/session';

// Verticales soportados por el backend (coach|gym|studio|education|nutrition|other, ver 0030).
const VERTICALS: { value: string; label: string; icon: string; hint: string }[] = [
  { value: 'coach', label: 'Coach', icon: 'user', hint: 'Entrenador personal' },
  { value: 'gym', label: 'Gimnasio', icon: 'dumbbell', hint: 'Gimnasio o centro' },
  { value: 'studio', label: 'Estudio', icon: 'store', hint: 'Baile, yoga, pilates' },
  { value: 'education', label: 'Educación', icon: 'graduation-cap', hint: 'Tutor o profesor' },
  { value: 'nutrition', label: 'Nutrición', icon: 'apple', hint: 'Nutricionista' },
  { value: 'other', label: 'Otro', icon: 'briefcase', hint: 'Otro tipo de negocio' },
];

type Mode = 'negocio' | 'cliente';

export function Registro() {
  const { registerBusiness, registerClient } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<Mode>(params.get('tipo') === 'cliente' ? 'cliente' : 'negocio');
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('gym');
  const [referralCode, setReferralCode] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const baseValid = Boolean(name && email && password.length >= 8);
  const canContinue = baseValid && Boolean(orgName);
  const canCreateClient = baseValid;

  async function onCreate() {
    setError(null);
    setBusy(true);
    try {
      const session = await registerBusiness({ name, email, password, orgName, orgType, country: 'EC', referralCode: referralCode || undefined });
      nav(homePathFor(session), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear la cuenta');
    } finally {
      setBusy(false);
    }
  }

  async function onCreateClient() {
    setError(null);
    setBusy(true);
    try {
      const session = await registerClient({ name, email, password, inviteCode: inviteCode || undefined });
      nav(homePathFor(session), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear la cuenta');
    } finally {
      setBusy(false);
    }
  }

  function pick(next: Mode) {
    setMode(next);
    setStep(1);
    setError(null);
  }

  return (
    <div className="gp-light" style={{ minHeight: '100vh', background: 'var(--bg)', display: 'grid', placeItems: 'center', padding: 24 }}>
      <Card style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-strong)' }}>Get<span style={{ color: 'var(--brand-green)' }}>Pay</span>93</span>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '12px 0 4px' }}>
          <FilterChip label="Tengo un negocio" icon={null} active={mode === 'negocio'} disabled={busy} onClick={() => pick('negocio')} />
          <FilterChip label="Soy cliente" icon={null} active={mode === 'cliente'} disabled={busy} onClick={() => pick('cliente')} />
        </div>

        {mode === 'negocio' ? (
          <>
            <p style={{ textAlign: 'center', color: 'var(--text-hint)', marginTop: 0, fontSize: 13 }}>Paso {step} de 2</p>

            {step === 1 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Input placeholder="Tu nombre" value={name} onChange={(e) => setName(e.currentTarget.value)} />
                <Input placeholder="Correo" type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
                <Input placeholder="Contraseña (mín. 8)" type="password" value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
                <Input placeholder="Nombre del negocio" value={orgName} onChange={(e) => setOrgName(e.currentTarget.value)} />
                <Button fullWidth disabled={!canContinue} onClick={() => setStep(2)}>Continuar</Button>
                <p style={{ textAlign: 'center', margin: 0, fontSize: 13 }}>¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link></p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <p style={{ margin: 0, fontWeight: 700, color: 'var(--text-body)' }}>¿Qué tipo de negocio es?</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {VERTICALS.map((v) => {
                    const on = v.value === orgType;
                    return (
                      <button key={v.value} onClick={() => setOrgType(v.value)} style={{ textAlign: 'left', padding: 14, borderRadius: 12, cursor: 'pointer', background: on ? 'var(--brand-green-soft)' : 'var(--surface)', border: `1px solid ${on ? 'var(--brand-green)' : 'var(--border)'}`, fontFamily: 'var(--font-sans)' }}>
                        <Icon name={v.icon} size={20} color={on ? 'var(--brand-green)' : 'var(--text-faint)'} />
                        <div style={{ fontWeight: 700, marginTop: 6, color: 'var(--text-strong)' }}>{v.label}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>{v.hint}</div>
                      </button>
                    );
                  })}
                </div>
                <Input placeholder="Código de referido (opcional)" value={referralCode} onChange={(e) => setReferralCode(e.currentTarget.value)} />
                {error ? <p style={{ margin: 0, color: 'var(--status-overdue)', fontSize: 13, fontWeight: 600 }}>{error}</p> : null}
                <div style={{ display: 'flex', gap: 10 }}>
                  <Button variant="outline" onClick={() => setStep(1)}>Atrás</Button>
                  <Button fullWidth disabled={busy} onClick={onCreate}>{busy ? 'Creando…' : 'Crear cuenta'}</Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 12 }}>
            <Input placeholder="Tu nombre" value={name} onChange={(e) => setName(e.currentTarget.value)} />
            <Input placeholder="Correo" type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
            <Input placeholder="Contraseña (mín. 8)" type="password" value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
            <Input placeholder="Código de invitación (opcional)" value={inviteCode} onChange={(e) => setInviteCode(e.currentTarget.value.toUpperCase())} />
            <p style={{ margin: 0, fontSize: 12, color: 'var(--text-hint)' }}>Si tu negocio te dio un código, pégalo aquí. Si no, podrás unirte después.</p>
            {error ? <p style={{ margin: 0, color: 'var(--status-overdue)', fontSize: 13, fontWeight: 600 }}>{error}</p> : null}
            <Button fullWidth disabled={!canCreateClient || busy} onClick={onCreateClient}>{busy ? 'Creando…' : 'Crear cuenta'}</Button>
            <p style={{ textAlign: 'center', margin: 0, fontSize: 13 }}>¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link></p>
          </div>
        )}
      </Card>
    </div>
  );
}
